import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'utils/cn';

/**
 * One placeholder row, drawn at the same 28px tile and two-line column the real row uses.
 *
 * The bar widths vary per row on purpose: five identical rows read as a rendering fault rather than
 * as a list arriving.
 */
function SkeletonRow({ title, secondary }: { title: string; secondary: string }) {
    return (
        <div className="flex items-start gap-2.5 px-3.5 py-2">
            <Skeleton className="bg-grey-200 size-7 flex-none rounded-[8px]" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-[3px]">
                <Skeleton className={cn('bg-grey-200 h-3 rounded-[4px]', title)} />
                <Skeleton className={cn('bg-grey-100 h-[9px] rounded-[4px]', secondary)} />
            </div>
        </div>
    );
}

function SkeletonHeading({ width }: { width: string }) {
    return (
        <div className="flex items-center justify-between px-3.5 pt-2.5 pb-1">
            <Skeleton className={cn('bg-grey-200 h-[9px] rounded-[4px]', width)} />
            <Skeleton className="bg-grey-100 h-[9px] w-10 rounded-[4px]" />
        </div>
    );
}

/**
 * What the dropdown shows between the query passing the minimum length and the results landing.
 *
 * It draws the shape of the answer — two sections and five rows — and not a spinner, so nothing
 * jumps when the real list replaces it. Recently viewed never shows one: that list is local and is
 * already there.
 */
export function GlobalSearchSkeleton() {
    return (
        <div aria-hidden>
            <SkeletonHeading width="w-[108px]" />
            <SkeletonRow title="w-[62%]" secondary="w-[38%]" />
            <SkeletonRow title="w-[48%]" secondary="w-[56%]" />
            <SkeletonRow title="w-[70%]" secondary="w-[30%]" />
            <div className="border-grey-200 border-t">
                <SkeletonHeading width="w-[74px]" />
                <SkeletonRow title="w-[54%]" secondary="w-[44%]" />
                <SkeletonRow title="w-[66%]" secondary="w-[34%]" />
            </div>
            <div className="border-grey-200 bg-grey-50 flex justify-center border-t px-3.5 py-[11px]">
                <Skeleton className="bg-grey-200 h-[11px] w-[168px] rounded-[4px]" />
            </div>
        </div>
    );
}
